/**
 * IMEI selection for a serialized line.
 *
 * The list comes from the server, not the cached catalogue: which specific handsets are on the
 * shelf changes with every sale on every till, and a stale IMEI is a sale that fails at submit
 * with the customer waiting. Search within it is local, because the salesperson is usually
 * reading the last four digits off a box.
 */

import { useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { useAvailableUnitsQuery, type ShopId, type UnitId, type VariantId } from '@pos/shared';

import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Identifier,
  Sheet,
  SkeletonRows,
  cn,
} from '@/shared/ui';
import { useBarcodeScanner } from '@/shared/lib/useBarcodeScanner';

export type SerialPickerSheetProps = {
  open: boolean;
  shopId: ShopId | null;
  variantId: VariantId | null;
  displayName: string;
  /** Already in the cart, so the same handset cannot be sold twice on one receipt. */
  takenUnitIds: readonly UnitId[];
  onPick: (unit: { id: UnitId; serialNo: string }) => void;
  onDismiss: () => void;
};

export function SerialPickerSheet({
  open,
  shopId,
  variantId,
  displayName,
  takenUnitIds,
  onPick,
  onDismiss,
}: SerialPickerSheetProps) {
  const [term, setTerm] = useState('');

  const { data, isLoading, error, refetch } = useAvailableUnitsQuery(
    shopId === null || variantId === null || !open ? skipToken : { shopId, variantId },
  );

  const units = useMemo(() => {
    const available = (data?.items ?? []).filter((unit) => !takenUnitIds.includes(unit.id));
    const needle = term.trim().toLowerCase();

    if (needle.length === 0) return available;
    return available.filter((unit) => unit.serialNo.toLowerCase().includes(needle));
  }, [data, takenUnitIds, term]);

  // A scanner pointed at the handset's own barcode picks it directly — the fastest path, and the
  // one that cannot pick the wrong box.
  useBarcodeScanner({
    enabled: open,
    onScan: (code) => {
      const match = (data?.items ?? []).find((unit) => unit.serialNo === code.trim());
      if (match) onPick({ id: match.id, serialNo: match.serialNo });
      else setTerm(code);
    },
  });

  return (
    <Sheet open={open} onDismiss={onDismiss} title="Choose a handset">
      <p className="mb-4 text-sm text-content-muted">{displayName}</p>

      <Field
        label="IMEI"
        placeholder="Type the last digits, or scan"
        value={term}
        inputMode="numeric"
        autoComplete="off"
        onChange={(event) => {
          setTerm(event.target.value);
        }}
      />

      <div className="mt-4">
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : error !== undefined ? (
          <ErrorState
            title="The handset list could not be loaded"
            body="This needs the network — IMEIs change with every sale, so they are never served from the offline copy."
            onRetry={() => void refetch()}
          />
        ) : units.length === 0 ? (
          <EmptyState
            title={term.length > 0 ? 'No IMEI matches that' : 'No handsets in stock here'}
            body={
              term.length > 0
                ? 'Check the digits, or clear the box to see everything on the shelf.'
                : 'The count says this shop has none of these. Receive stock first, or correct the count.'
            }
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {units.map((unit) => (
              <li key={unit.id}>
                <Button
                  variant="quiet"
                  fullWidth
                  className={cn('justify-between')}
                  onClick={() => {
                    onPick({ id: unit.id, serialNo: unit.serialNo });
                  }}
                >
                  <Identifier value={unit.serialNo} />
                  <span className="text-xs font-medium text-content-muted">
                    {conditionLabel(unit.condition)}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}

function conditionLabel(condition: string): string {
  switch (condition) {
    case 'new':
      return 'New';
    case 'used_a':
      return 'Used · A';
    case 'used_b':
      return 'Used · B';
    case 'used_c':
      return 'Used · C';
    default:
      return condition;
  }
}
